export type ValidationErrors = Record<string, string | undefined>;
export type ValidationRule<T, K extends keyof T> = {
  field: K;
  getValidationErrors?: (value?: T[K]) => string | undefined;
};
export type ValidationRules<T, K extends keyof T> = Array<ValidationRule<T, K>>;

/**
 * Validates an object against an array of validation rules.
 * Returns a map of field names to validation error messages.
 *
 * @template T - The type of the object to validate.
 * @template K - The keys of the object to validate.
 * @param {() => ValidationRules<T, K>} validationRules A function that returns an array of validation rules.
 * @param {T} fields - The fields to validate.
 * @returns {Record<K, string>} - A map of field names to validation error messages.
 */
export const validateAll = <T extends Record<string, unknown>, K extends keyof T>(
  validationRules: () => ValidationRules<T, K>,
  fields: T
): Record<K, string> => {
  const errors: Partial<Record<K, string>> = {};
  const rules = validationRules();
  for (const rule of rules) {
    const fieldName = rule.field as K;
    const value = fields[fieldName];
    const validationError = rule.getValidationErrors?.(value);

    if (validationError) {
      errors[fieldName] = validationError;
    }
  }
  return errors as Record<K, string>;
};

/**
 * Validates a single field against an array of validation rules.
 * Returns a validation error message if the field fails validation, or `undefined` if it passes validation or if no rules apply to the field.
 *
 * @template T - The type of the object to validate.
 * @template K - The key of the field to validate.
 * @param {() => ValidationRules<T, K>} validationRules A function that returns an array of validation rules.
 * @param {K} field - The field to validate.
 * @param {T[K]} [value] - The value of the field to validate.
 * @returns {string | undefined} - A validation error message if the field fails validation, or `undefined` if it passes validation or if no rules apply to the field.
 */
export const validateField = <T, K extends keyof T>(
  validationRules: () => ValidationRules<T, K>,
  field: K,
  value?: T[K]
): string | undefined => {
  const rules = validationRules().filter((rule) => rule.field === field);

  if (rules.length === 0) {
    return undefined;
  }

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return rules[0].getValidationErrors?.(value);
};
